import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminAuth } from "../../lib/AdminAuthContext";
import { formatApiErrorDetail } from "../../lib/adminApi";
import { LOGO_URL } from "../../lib/site";

export default function AdminLogin() {
  const { login } = useAdminAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      const dest = loc.state?.from?.startsWith("/admin") ? loc.state.from : "/admin";
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="CashPark" className="h-14 w-14 mx-auto" />
          <h1 className="font-display text-2xl font-bold text-[#0B192C] mt-3">Admin Console</h1>
          <p className="text-sm text-slate-500 mt-1">Sign in to manage CashPark</p>
        </div>
        <form onSubmit={onSubmit} className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm space-y-4" data-testid="admin-login-form">
          <div>
            <Label htmlFor="email">Email</Label>
            <div className="relative mt-1.5">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@..." data-testid="admin-email-input" className="pl-9 h-11 rounded-xl" />
            </div>
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative mt-1.5">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="admin-password-input" className="pl-9 h-11 rounded-xl" />
            </div>
          </div>
          <Button type="submit" disabled={busy} data-testid="admin-login-submit" className="w-full h-11 rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
